import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import axiosInstance from '../services/axiosInstance'

const AuthContext = createContext(null)

const TOKEN_KEY = 'campusready_token'
const USER_KEY  = 'campusready_user'

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY))
  const [user, setUser]   = useState(() => {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? JSON.parse(raw) : null
  })

  useEffect(() => {
    // We only fetch once on mount if there's a token
    const currentToken = localStorage.getItem(TOKEN_KEY)
    if (currentToken) {
      axiosInstance.get('/api/users/me')
        .then(res => {
          if (res.data.success && res.data.user) {
            setUser(res.data.user)
            localStorage.setItem(USER_KEY, JSON.stringify(res.data.user))
            
            if (res.data.token) {
              setToken(res.data.token)
              localStorage.setItem(TOKEN_KEY, res.data.token)
            }
          } else {
            // Force logout if user is strictly not found
            localStorage.removeItem(TOKEN_KEY)
            localStorage.removeItem(USER_KEY)
            setToken(null)
            setUser(null)
          }
        })
        .catch(err => {
          console.error("Failed to refresh user profile silently:", err)
          if (err.response && (err.response.status === 401 || err.response.status === 404)) {
            localStorage.removeItem(TOKEN_KEY)
            localStorage.removeItem(USER_KEY)
            setToken(null)
            setUser(null)
          }
        })
    }
  }, [])

  const login = useCallback((newToken, newUser) => {
    localStorage.setItem(TOKEN_KEY, newToken)
    localStorage.setItem(USER_KEY, JSON.stringify(newUser))
    setToken(newToken)
    setUser(newUser)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    setToken(null)
    setUser(null)
  }, [])

  const isAuthenticated = Boolean(token)

  return (
    <AuthContext.Provider value={{ token, user, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
